<script setup>
import { ref, watch, watchEffect, computed } from "vue";

// Props: modelValue for the full phone number (e.g., "+11234567890")
// or separate props for initial code and number if preferred.
const props = defineProps({
  modelValue: {
    // Full phone number string: dial_code + national_number
    type: String,
    default: "",
  },
  initialDialCode: {
    type: String,
    default: null,
  },
  initialNationalNumber: {
    type: String,
    default: "",
  },
  className: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "onUpdate"]);

const selectedPhoneCode = ref(null); // Stores the selected code object {name, flag, dial_code, display}
const nationalNumber = ref("");

const {
  data: phoneCodes,
  status,
  error,
} = await useFetch("/api/phone-codes", {
  lazy: true,
  transform: (codes) => {
    return codes.map((code) => ({
      name: code.name,
      flag: code.flag,
      dial_code: code.dial_code,
      display: `${code.flag} ${code.name.length > 16 ? code.name.substring(0, 20) + "..." : code.name} (${code.dial_code})`, // Used for display in Select
    }));
  },
});

// Initialize from modelValue or individual props
watchEffect(() => {
  if (phoneCodes.value && phoneCodes.value.length > 0) {
    if (props.modelValue) {
      let foundCode = null;
      // Try to find the longest matching dial_code from the beginning of modelValue
      for (const code of phoneCodes.value.sort(
        (a, b) => b.dial_code.length - a.dial_code.length,
      )) {
        if (props.modelValue.startsWith(code.dial_code)) {
          foundCode = code;
          break;
        }
      }
      if (foundCode) {
        selectedPhoneCode.value = foundCode;
        nationalNumber.value = props.modelValue.substring(
          foundCode.dial_code.length,
        );
      } else {
        // Fallback if no code matches, or handle as an error/default
        nationalNumber.value = props.modelValue; // Assume it's all national number if no code found
      }
    } else if (props.initialDialCode) {
      const found = phoneCodes.value.find(
        (c) => c.dial_code === props.initialDialCode,
      );
      if (found) selectedPhoneCode.value = found;
      nationalNumber.value = props.initialNationalNumber || "";
    }
  }
});

// Combine selected code and national number to emit the full phone number
const fullPhoneNumber = computed(() => {
  if (selectedPhoneCode.value && selectedPhoneCode.value.dial_code) {
    return `${selectedPhoneCode.value.dial_code}${nationalNumber.value}`;
  }
  return nationalNumber.value; // Or handle as incomplete if no code selected
});

// Watch for changes and emit the full phone number
watch([selectedPhoneCode, nationalNumber], () => {
  emit("update:modelValue", fullPhoneNumber.value);
});
</script>

<template>
  <div class="flex items-center gap-2">
    <div v-if="status == 'pending'" class="text-xs">Loading codes...</div>
    <Select
      v-else
      v-model="selectedPhoneCode"
      :options="phoneCodes"
      filter
      :filterFields="['name', 'dial_code', 'display']"
      optionLabel="display"
      placeholder="Code"
      class="!w-[130px] text-xs lg:text-sm phone-code-select"
    >
      <template #option="{ option }">
        <span class="text-[#4B5563] text-xs lg:text-sm">
          {{ option.display }}
        </span>
      </template>
      <template #value="{ value, placeholder }">
        <span v-if="value?.flag" class="text-[#4B5563] text-xs lg:text-sm">
          {{ value.flag }} {{ value.dial_code }}
        </span>
        <span v-else class="text-gray-400 text-xs lg:text-sm">
          {{ placeholder }}
        </span>
      </template>
    </Select>

    <InputText
      v-model="nationalNumber"
      placeholder="Phone number"
      class="text-xs lg:text-sm flex-grow phone-number-input"
      :class="className"
      @keyup.enter="emit('onUpdate')"
      type="tel"
    />
  </div>
</template>

<style scoped>
.phone-code-select ::v-deep(.p-inputtext) {
  padding-top: 0.5rem; /* Adjust padding as needed */
  padding-bottom: 0.5rem;
}
.phone-number-input {
  padding-top: 0.5rem; /* Adjust padding as needed */
  padding-bottom: 0.5rem;
}
/* Add any additional styling if necessary */
</style>
